/**
 * A gallery visit from Canva lands on the products that can hold the parked design — the "Match
 * SKU" view — rather than on a product category.
 *
 * The Canva org's SKUs live in their own product-category galleries, so landing on any one of
 * them shows a single category and hides every other product that fits. This view gathers the
 * matches from across all of them into one list, with a way out to the full company gallery.
 *
 * Carried in the URL rather than in component state so the choice survives a refresh and the
 * browser's back button steps between the two views.
 */
const CANVA_ALL_PRODUCTS_PARAM = 'canvaAllProducts';

/**
 * Category the buyer opened from the full gallery, when it holds more than one product and the
 * publish still needs to know which one. Same reasoning as above for living in the URL: the pick
 * is a step the buyer can back out of.
 */
const CANVA_CATEGORY_PARAM = 'canvaCategoryId';

/** True once the buyer has chosen to browse the whole company gallery instead of the matches. */
export const parseCanvaAllProducts = (search: string): boolean =>
  new URLSearchParams(search).get(CANVA_ALL_PRODUCTS_PARAM) === '1';

/** The same location with the full company gallery shown; every other param is preserved. */
export const withCanvaAllProducts = (search: string): string => {
  const params = new URLSearchParams(search);
  params.set(CANVA_ALL_PRODUCTS_PARAM, '1');
  // Leaving a category behind is part of going back to the full gallery -- otherwise the link
  // out of the SKU picker lands on the picker again.
  params.delete(CANVA_CATEGORY_PARAM);
  return `?${params.toString()}`;
};

/**
 * Gallery label whose products the buyer is choosing between, or undefined when they are not in
 * a category. A value that is not a positive id is read as "no category" rather than trusted:
 * it reaches the API as a filter, and a hand-edited one would otherwise return the whole
 * catalogue under a category heading.
 */
export const parseCanvaCategoryId = (search: string): number | undefined => {
  const raw = new URLSearchParams(search).get(CANVA_CATEGORY_PARAM);
  if (!raw) {
    return undefined;
  }
  const id = Number(raw);
  return Number.isInteger(id) && id > 0 ? id : undefined;
};

/** The same location showing one category's products; every other param is preserved. */
export const withCanvaCategory = (search: string, categoryId: number): string => {
  const params = new URLSearchParams(search);
  // The picker is reached from the full gallery and the link back out is the same one, so the
  // browse-all flag travels with it.
  params.set(CANVA_ALL_PRODUCTS_PARAM, '1');
  params.set(CANVA_CATEGORY_PARAM, String(categoryId));
  return `?${params.toString()}`;
};
